import { Colli } from '@frontend/colli/types';
import { Package, PackageStateName } from '@frontend/package/types';

export interface ProofOfIdentityColliGroup {
    colliId: string | null;
    /** Colli label; `null` when packages have no colli_id (ungrouped fallback). */
    label: string | null;
    packages: Package[];
}

export type ProofOutcome = 'completed' | 'failed' | 'skipped';

export function getPackagesRequiringProof(packages: Package[]): Package[] {
    return packages.filter((pkg) => pkg.require_proof_of_identity);
}

export function shouldGroupByColli(colliCount: number): boolean {
    return colliCount >= 2;
}

/** Terminal outcomes shown on the proof-of-identity card (pending/in-progress stay hidden). */
export function getProofOutcome(state: PackageStateName): ProofOutcome | null {
    switch (state) {
        case PackageStateName.PACKAGE_DROP_OFF_DONE:
        case PackageStateName.PACKAGE_PICK_UP_DONE:
        case PackageStateName.PACKAGE_PASS_THROUGH_DONE:
        case PackageStateName.PACKAGE_AUDIT_DONE:
            return 'completed';
        case PackageStateName.PACKAGE_DROP_OFF_FAILED:
        case PackageStateName.PACKAGE_PICK_UP_FAILED:
        case PackageStateName.PACKAGE_PASS_THROUGH_FAILED:
        case PackageStateName.PACKAGE_AUDIT_FAILED:
            return 'failed';
        case PackageStateName.PACKAGE_DROP_OFF_SKIPPED:
        case PackageStateName.PACKAGE_PICK_UP_SKIPPED:
            return 'skipped';
        default:
            return null;
    }
}

/**
 * Groups ID-required packages by colli. Only includes collis that have at least one
 * such package. Packages without `colli_id` are returned in a final ungrouped group.
 */
export function groupPackagesByColli(packages: Package[], collis: Colli[]): ProofOfIdentityColliGroup[] {
    const colliById = new Map(collis.map((colli) => [colli.id, colli]));
    const groupsByColliId = new Map<string, ProofOfIdentityColliGroup>();
    const ungrouped: Package[] = [];

    for (const pkg of packages) {
        if (!pkg.colli_id) {
            ungrouped.push(pkg);
            continue;
        }
        const existing = groupsByColliId.get(pkg.colli_id);
        if (existing) {
            existing.packages.push(pkg);
            continue;
        }
        const colli = colliById.get(pkg.colli_id);
        groupsByColliId.set(pkg.colli_id, {
            colliId: pkg.colli_id,
            label: colli?.label ?? pkg.colli_id,
            packages: [pkg]
        });
    }

    const ordered: ProofOfIdentityColliGroup[] = [];
    for (const colli of collis) {
        const group = groupsByColliId.get(colli.id);
        if (group) {
            ordered.push(group);
            groupsByColliId.delete(colli.id);
        }
    }
    for (const group of groupsByColliId.values()) {
        ordered.push(group);
    }
    if (ungrouped.length > 0) {
        ordered.push({ colliId: null, label: null, packages: ungrouped });
    }
    return ordered;
}
